<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToast } from './composables/toast';
import { getI18n } from '@/helpers/i18n';
import Guest from '@/views/guest/Guest.vue';
import AuthRootView from './views/auth/AuthRootView.vue';
import { isAuthenticated, useGlobals } from './helpers/globals';

const { queue: messageQueue } = useToast();

type Browser = {
    name: string,
    version: number
}

function isSupported(browser: Browser) {
    let supported = false;
    switch (browser.name) {
        case 'Chrome':
        case 'Edge':
            supported = browser.version > 90;
            break;
        case 'Firefox':
            supported = browser.version > 90;
            break;
        case 'Opera':
            supported = browser.version > 80;
            break;
        case 'Safari':
            supported = browser.version > 14;
            break;
        case 'MSIE':
        case 'IE':
            supported = false;
            break;
        default:
            supported = true;
    }
    return supported;
}

function getBrowser(): Browser {
    const ua = navigator.userAgent;
    let tem;
    let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
    if (/trident/i.test(M[1])) {
        tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
        return { name: 'IE', version: Number.parseInt(tem[1], 10) || 0 };
    }
    if (M[1] === 'Chrome') {
        tem = ua.match(/\bOPR\/(\d+)/);
        if (tem !== null) {
            return { name: 'Opera', version: Number.parseInt(tem[1], 10) || 0 };
        }
    }
    if (window.navigator.userAgent.indexOf('Edge') > -1) {
        tem = ua.match(/Edge\/(\d+)/);
        if (tem !== null) {
            return { name: 'Edge', version: Number.parseInt(tem[1], 10) || 0 };
        }
    }
    M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
    if ((tem = ua.match(/version\/(\d+)/i)) !== null) {
        M.splice(1, 1, tem[1]);
    }
    return {
        name: M[0],
        version: +M[1]
    };
}

const notSupportedMsg = ref('');
const showNotSupported = ref(false);

onMounted(() => {
    const browser = getBrowser();
    if (!isSupported(browser)) {
        notSupportedMsg.value = getI18n().t('browser_error_msg');
        if (browser.name === 'IE' || browser.name === 'MSIE') {
            notSupportedMsg.value = getI18n().t('ie_error_msg');
        }
        showNotSupported.value = true;
    }
})

const customer = useGlobals().customer;
</script>

<template>
    <v-snackbar-queue v-model="messageQueue" location="top center" close-on-back></v-snackbar-queue>
    <div :class="'customer-' + customer">
        <v-snackbar v-model="showNotSupported" style="z-index: 1000;" :timeout="-1" absolute centered color="#d50000"
            elevation="24" top>
            <div style="display: flex;">
                <v-icon style="margin-right: 10px;">fas fa-exclamation-triangle</v-icon>
                <div>
                    <h3>
                        <span v-html="notSupportedMsg"></span>
                    </h3>
                </div>
            </div>
        </v-snackbar>
        <Guest v-if="!isAuthenticated()" />
        <AuthRootView v-else />
    </div>
</template>

<style scoped></style>
